import { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner, Table } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScissors, faTrash, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { API } from '../../config/api';

const API_BASE = `${API.BACKEND}/api/v1`;

const fmtDur = sec => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};
const hhmm = ts => (ts ? dayjs(Number(ts)).format('HH:mm') : '—');
const toLocalInput = ts => (ts ? dayjs(Number(ts)).format('YYYY-MM-DDTHH:mm') : '');

/**
 * Fix who produced a drum, after the fact — for when nobody logged in.
 *
 * This edits ATTRIBUTION, not measurement. The spans arrive with the time and
 * metres the worker measured; the only things editable are the name, the shift,
 * and where one span is cut in two. The backend rejects anything that changes
 * how much work was done.
 */
const EditOperatorsModal = ({ tokenId, show, onHide, actor = null }) => {
  const queryClient = useQueryClient();
  const [spans, setSpans] = useState([]);
  const [reason, setReason] = useState('');
  const [splitFor, setSplitFor] = useState(null); // index being split
  const [splitAt, setSplitAt] = useState('');
  const [error, setError] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['token-operators', tokenId],
    queryFn: async () =>
      (await axios.get(`${API_BASE}/production-metrics/token/${tokenId}/operators`)).data?.data,
    enabled: !!tokenId && show
  });

  const { data: operatorList } = useQuery({
    queryKey: ['operators', 'active'],
    // every active operator, deliberately unfiltered by shift: the case being
    // fixed is usually someone covering a shift that was not theirs
    queryFn: async () => (await axios.get(`${API_BASE}/operators?active=true`)).data?.data || [],
    enabled: show
  });

  useEffect(() => {
    if (data?.operators) {
      setSpans(data.operators.map(s => ({ ...s })));
      setReason('');
      setError(null);
      setSplitFor(null);
    }
  }, [data]);

  const names = useMemo(
    () => [...new Set((operatorList || []).map(o => o.name).filter(Boolean))].sort(),
    [operatorList]
  );

  const totals = useMemo(
    () =>
      spans.reduce(
        (a, s) => ({
          sec: a.sec + (Number(s.runningSec) || 0),
          m: a.m + (Number(s.meters) || 0)
        }),
        { sec: 0, m: 0 }
      ),
    [spans]
  );

  const setSpan = (i, patch) =>
    setSpans(prev => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  // Cut a span in two at a moment in time. Metres divide in proportion to time —
  // an approximation, since production is not uniform, so both halves are marked
  // estimated. The second part takes the remainder so the totals cannot drift.
  const applySplit = () => {
    const i = splitFor;
    const s = spans[i];
    const at = dayjs(splitAt).valueOf();

    if (!s || !Number.isFinite(at)) return setError('Pick a valid handover time.');
    if (!(at > Number(s.startedAt)) || !(at < Number(s.endedAt))) {
      return setError(
        `The handover must fall between ${hhmm(s.startedAt)} and ${hhmm(s.endedAt)}.`
      );
    }

    const fraction = (at - Number(s.startedAt)) / (Number(s.endedAt) - Number(s.startedAt));
    const totalSec = Math.max(0, Math.round(Number(s.runningSec) || 0));
    const totalM = Math.max(0, Number(s.meters) || 0);
    const leftSec = Math.round(totalSec * fraction);
    const leftM = Math.round(totalM * fraction * 100) / 100;

    const left = { ...s, endedAt: at, runningSec: leftSec, meters: leftM, estimated: true };
    const right = {
      ...s,
      operator: null,
      startedAt: at,
      runningSec: totalSec - leftSec,
      meters: Math.round((totalM - leftM) * 100) / 100,
      estimated: true
    };

    setSpans(prev => [...prev.slice(0, i), left, right, ...prev.slice(i + 1)]);
    setSplitFor(null);
    setSplitAt('');
    setError(null);
  };

  // Undo a split: give the neighbour's time and metres back to this span.
  const mergeWithNext = i => {
    setSpans(prev => {
      const a = prev[i];
      const b = prev[i + 1];
      if (!a || !b) return prev;
      const merged = {
        ...a,
        endedAt: b.endedAt,
        runningSec: (Number(a.runningSec) || 0) + (Number(b.runningSec) || 0),
        meters: Math.round(((Number(a.meters) || 0) + (Number(b.meters) || 0)) * 100) / 100
      };
      return [...prev.slice(0, i), merged, ...prev.slice(i + 2)];
    });
  };

  const mutation = useMutation({
    mutationFn: body =>
      axios.patch(`${API_BASE}/production-metrics/token/${tokenId}/operators`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['token-operators', tokenId] });
      queryClient.invalidateQueries({ queryKey: ['execution-logs'] });
      queryClient.invalidateQueries({ queryKey: ['production-metrics'] });
      onHide();
    },
    onError: err =>
      setError(err?.response?.data?.error || err.message || 'Could not save')
  });

  const save = () => {
    setError(null);
    if (!spans.some(s => s.operator)) {
      return setError('Assign at least one operator, or close without saving.');
    }
    mutation.mutate({ operators: spans, actor, reason: reason.trim() || null });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-8">
          Who produced this drum?
          {data?.productLabel ? (
            <span className="text-700 fs-9 ms-2">
              {data.productLabel} · {data.color || '—'}
            </span>
          ) : null}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className="d-flex align-items-center gap-2 py-4">
            <Spinner animation="border" size="sm" /> <span className="fs-9">Loading…</span>
          </div>
        ) : isError ? (
          <Alert variant="danger" className="fs-9 mb-0">
            No production record found for this drum.
          </Alert>
        ) : (
          <>
            <p className="fs-9 text-700">
              The times and metres below were measured by the machine and cannot be
              changed here — only <b>who</b> did the work. Split a span if two people
              shared the drum.
            </p>

            <Table size="sm" className="fs-9 align-middle">
              <thead>
                <tr className="text-700">
                  <th style={{ minWidth: 180 }}>Operator</th>
                  <th style={{ width: 90 }}>Shift</th>
                  <th>From</th>
                  <th>To</th>
                  <th className="text-end">Running</th>
                  <th className="text-end">Metres</th>
                  <th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {spans.map((s, i) => (
                  <tr key={i}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={s.operator || ''}
                        onChange={e => setSpan(i, { operator: e.target.value || null })}
                        className={!s.operator ? 'border-warning' : undefined}
                      >
                        <option value="">— unassigned —</option>
                        {names.map(n => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                        {s.operator && !names.includes(s.operator) ? (
                          <option value={s.operator}>{s.operator}</option>
                        ) : null}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={s.shift || ''}
                        onChange={e => setSpan(i, { shift: e.target.value || null })}
                      >
                        <option value="">—</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                      </Form.Select>
                    </td>
                    <td className="font-monospace text-700">{hhmm(s.startedAt)}</td>
                    <td className="font-monospace text-700">{hhmm(s.endedAt)}</td>
                    <td className="font-monospace text-end text-700">{fmtDur(s.runningSec)}</td>
                    <td className="font-monospace text-end text-700">
                      {Number(s.meters || 0).toFixed(0)} m
                      {s.estimated ? (
                        <div className="fs-10 text-warning" title="Split by hand — metres divided in proportion to time">
                          estimated
                        </div>
                      ) : null}
                    </td>
                    <td className="text-end">
                      {s.startedAt && s.endedAt ? (
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 me-2 text-700"
                          title="Split between two operators"
                          onClick={() => {
                            setSplitFor(i);
                            setSplitAt(
                              toLocalInput(
                                Number(s.startedAt) +
                                  (Number(s.endedAt) - Number(s.startedAt)) / 2
                              )
                            );
                            setError(null);
                          }}
                        >
                          <FontAwesomeIcon icon={faScissors} />
                        </Button>
                      ) : null}
                      {i < spans.length - 1 ? (
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 text-700"
                          title="Undo the split — merge with the span below"
                          onClick={() => mergeWithNext(i)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {splitFor != null ? (
              <div className="border rounded p-3 mb-3 bg-body-tertiary">
                <div className="fs-9 fw-semibold mb-2">
                  Handover time — the first operator works up to this moment
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Control
                    type="datetime-local"
                    size="sm"
                    style={{ maxWidth: 240 }}
                    value={splitAt}
                    onChange={e => setSplitAt(e.target.value)}
                  />
                  <Button size="sm" variant="primary" onClick={applySplit}>
                    Split
                  </Button>
                  <Button size="sm" variant="link" onClick={() => setSplitFor(null)}>
                    Cancel
                  </Button>
                  <span className="fs-10 text-700">
                    between {hhmm(spans[splitFor]?.startedAt)} and {hhmm(spans[splitFor]?.endedAt)}
                  </span>
                </div>
              </div>
            ) : null}

            <div className="d-flex justify-content-between fs-10 text-700 mb-3">
              <span>
                Total: <b>{fmtDur(totals.sec)}</b> · <b>{totals.m.toFixed(0)} m</b>
              </span>
              {data?.producedLength ? (
                <span>Drum produced {Number(data.producedLength).toFixed(0)} m</span>
              ) : null}
            </div>

            <Form.Group>
              <Form.Label className="fs-9 fw-semibold text-700 mb-1">
                Why (optional)
              </Form.Label>
              <Form.Control
                size="sm"
                placeholder="e.g. operator forgot to log in on the night shift"
                value={reason}
                onChange={e => setReason(e.target.value)}
              />
            </Form.Group>

            {data?.editedAt ? (
              <div className="fs-10 text-700 mt-2">
                Last corrected by <b>{data.editedBy || 'unknown'}</b> on{' '}
                {dayjs(data.editedAt).format('DD MMM YYYY, HH:mm')}
                {data.editReason ? ` — ${data.editReason}` : ''}
              </div>
            ) : null}

            {error ? (
              <Alert variant="danger" className="fs-9 mt-3 mb-0 py-2">
                <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
                {error}
              </Alert>
            ) : null}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="link" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={save} disabled={mutation.isPending || isLoading}>
          {mutation.isPending ? 'Saving…' : 'Save attribution'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditOperatorsModal;
